import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { LearningInterventionRow } from '../types';

// Learning loop (spec 9.6): the FAST, per-intervention grid. Isolated + independently failable
// by design — it owns its own loading / empty / error states, so if this endpoint fails or
// returns nothing the macro band above still renders. One server-aggregated call, already
// default-sorted to the fast, improving cohort.
//
// The real risk is the SPARSE state: on a fresh instance most rows have few closed
// prediction->outcome pairs, so they read "accumulating · N of M observations" rather than a
// blank or a misleading 0%. A row only earns its accuracy curve once it has enough observations.

const TREND_TAG: Record<string, string> = { improving: 'teal', declining: 'red', flat: 'muted' };

export default function LearningGrid() {
  const grid = useQuery({ queryKey: ['learning', 'interventions'], queryFn: api.learningInterventions });

  if (grid.isLoading) return <div className="small faint">Loading per-intervention grid…</div>;
  // Independently failable: a grid failure must NOT take down the macro band — this renders in
  // place of the grid only.
  if (grid.isError) return <div className="small faint">Per-intervention grid unavailable (the macro band above is unaffected).</div>;

  const rows = grid.data?.interventions ?? [];
  if (rows.length === 0) {
    return (
      <div className="panel">
        <div className="qhead"><span className="qdot" style={{ background: 'var(--blue)' }} />Per-intervention learning grid</div>
        <div className="small faint" style={{ lineHeight: 1.6 }}>
          No predictions have been recorded yet. As interventions predict and their outcomes land, rows
          appear here and accumulate toward a measurable accuracy curve — the fast cohort earns its trend
          over weeks.
        </div>
      </div>
    );
  }

  const tracking = rows.filter((r) => r.status === 'tracking').length;
  const accumulating = rows.length - tracking;

  return (
    <div className="panel">
      <div className="qhead">
        <span className="qdot" style={{ background: 'var(--blue)' }} />
        Per-intervention learning grid · {tracking} tracking · {accumulating} accumulating
      </div>
      <p className="small faint" style={{ marginTop: -4, marginBottom: 10 }}>
        High-frequency micro-decisions, default-sorted to the fast, improving cohort. Accumulating rows
        show how much data they have, not a number we can't yet trust.
      </p>
      <table>
        <thead>
          <tr>
            <th>Intervention</th>
            <th>Learning</th>
            <th className="center">Accuracy</th>
            <th className="center">Trend</th>
            <th className="center">Observations</th>
            <th className="center">Override rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <GridRow key={row.intervention_id} row={row} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GridRow({ row }: { row: LearningInterventionRow }) {
  const accumulating = row.status === 'accumulating';
  return (
    <tr>
      <td>
        <div style={{ fontWeight: 500, color: 'var(--ink)' }}>{row.name ?? row.intervention_id}</div>
        <div className="small faint mono">{row.intervention_id}</div>
      </td>
      <td className="small">{row.learning ?? '—'}</td>
      <td className="center">
        {accumulating ? (
          <span className="tag muted">accumulating</span>
        ) : (
          <b style={{ color: row.drift_flag ? 'var(--red)' : 'var(--olive)' }}>
            {row.accuracy_pct?.toFixed(0)}%{row.drift_flag ? ' ⚠' : ''}
          </b>
        )}
      </td>
      <td className="center">
        {accumulating ? <span className="small faint">—</span> : <span className={`tag ${TREND_TAG[row.trend] ?? 'muted'}`}>{row.trend}</span>}
      </td>
      <td className="center small">
        {/* N of M observations — closed pairs of total predictions made, so a sparse row reads
            honestly instead of as 0% accuracy. */}
        {row.sample_count} of {row.total_predictions}
        {accumulating && row.total_predictions > 0 ? <span className="faint"> · accumulating</span> : null}
      </td>
      <td className="center small">{(row.override_rate * 100).toFixed(0)}%</td>
    </tr>
  );
}
